import React, { useEffect, useMemo, useState } from "react";
import { Card, Typography, Tabs, Table, Select, Button, Modal, Space, Tag, message, Spin } from "antd";
import { PlusOutlined, MinusCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Icon } from "Components";
import { ColumnsType } from "antd/es/table";
import { getExternalMerchants, PciPortalProperty } from "Services/Api/PciCompliance/Mapping.Service";
import { PropertyGroups, TenantMapping } from "../Mapping";
import { debounce } from "lodash";

const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

interface MappingChange {
  id?: string;
  venzaProperty?: string | null;
  venzaPropertyId?: string | null;
  pciPortalProperty?: string | null;
  pciPortalPropertyId?: string | null;
  mid?: string | null;
  changeType?: "new" | "unchanged";
  action?: "" | "confirm" | "remove" | "remap" | null;
  isAddRow?: boolean;
}

interface ReviewMappingStep2Props {
  tenantInfo: TenantMapping;
  initialData: MappingChange[];
  unChangedData: MappingChange[];
  onNext: (prevChanges: MappingChange[]) => void;
  onBack: () => void;
  venzaProperties: PropertyGroups[];
}

const DEBOUNCE_TIME = 400;
export const ReviewMappingStep2: React.FC<ReviewMappingStep2Props> = ({ tenantInfo, initialData, onNext, onBack, venzaProperties, unChangedData }) => {

  const [changes, setChanges] = useState<MappingChange[]>([]);
  const [tempChanges, setTempChanges] = useState<MappingChange[]>([]);
  const [newRow, setNewRow] = useState<MappingChange>()
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const [pciPortalProperties, setPciPortalProperties] = useState<PciPortalProperty[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isRemovedModalVisible, setIsRemovedModalVisible] = useState(false);
  const [savedRowConfirmationModalVisible, setSavedRowConfirmationModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<MappingChange>();

  const visibleChanges = changes.filter(c => c.action !== "remove").filter(n => !isNaN(parseInt(n.id ? n.id : "")));
  const unchangedMappings = visibleChanges.filter(c => c.changeType === "unchanged" && !c.action).filter(n => !isNaN(parseInt(n.id ? n.id : "")));
  const pendingReview = visibleChanges.filter(c => c.changeType === "new" && !c.action).filter(n => !isNaN(parseInt(n.id ? n.id : "")));
  const removedChanges = changes.filter(c => c.action === "remove").filter(n => !isNaN(parseInt(n.id ? n.id : "")));
  const newCount = changes.length - initialData.length;
  const pendingCount = pendingReview.length;
  const removedCount = removedChanges.length;

  const handleVenzaChange = (record: MappingChange, value: string) => {
    setSelectedRow({ ...changes.find(c => c.venzaPropertyId === String(value)), venzaPropertyId: String(value), venzaProperty: venzaProperties.find(el => el.id === String(value))?.name, });
    setChanges(prev => prev.map(c => c.id === record.id ? { ...c, venzaProperty: venzaProperties.find(e => e.id === String(value))?.name, venzaPropertyId: String(value), } : c));
  };

  const handlePciChange = (record: MappingChange, value: string) => {
    setSelectedRow({ ...changes.find(c => c.mid === value), pciPortalPropertyId: value, pciPortalProperty: pciPortalProperties.find(el => el.mid === value)?.name, });
    setChanges(prev => prev.map(c => c.id === record.id ? { ...c, pciPortalProperty: pciPortalProperties.find(e => e.mid === value)?.name, pciPortalPropertyId: value, } : c));
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        handlePciPropertySearch(value);
      }, DEBOUNCE_TIME),
    []
  );

  const handleConfirmMappingChange = () => {
    setChanges([...changes.map(el =>
      el.id === selectedRow?.id ? { ...selectedRow } : { ...el })])
    setEditingId(null)
    setSavedRowConfirmationModalVisible(false)
  }

  const handleAddNewMapping = () => {
    setIsAddingNew(true)
    const newChange: MappingChange = {
      id: `new-${Date.now()}`,
      venzaProperty: null,
      venzaPropertyId: "",
      pciPortalPropertyId: "",
      pciPortalProperty: null,
      changeType: "new",
      action: null,
    };
    const addRowRemoved = [...changes, newChange].filter(val => !val.isAddRow)
    setChanges(addRowRemoved);
  };

  const handleContinue = () => {
    onNext(
      changes
        .filter(e => !!e.venzaProperty && !!e.pciPortalProperty)
        .map(e => ({
          ...e,
          venzaProperty: e.venzaProperty ?? undefined,
          pciPortalProperty: e.pciPortalProperty ?? undefined,
        })));

  }

  const handleNewAddRow = (record: MappingChange) => {

    let filtered = changes.filter(
      (item) =>
        item.id !== "add-row" &&
        item.venzaProperty &&
        item.pciPortalProperty
    );

    const uniqueMap = new Map<string, MappingChange>();

    filtered.forEach((item) => {
      if (!item.id) return;
      if (!uniqueMap.has(item.id)) {
        uniqueMap.set(item.id, item);
      }
    });
    filtered = Array.from(uniqueMap.values());

    const mappings = [...filtered, record];

    const numericIds = mappings
      .map((el) => parseInt(el.id || "0", 10))
      .filter((num) => !isNaN(num));
    const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;

    const updatedList: MappingChange[] = mappings.map((el) => {
      if (el.id?.startsWith("new") || el.id === record.id) {
        return { ...el, venzaProperty: newRow?.venzaProperty, venzaPropertyId: newRow?.venzaPropertyId, pciPortalPropertyId: newRow?.pciPortalPropertyId, pciPortalProperty: newRow?.pciPortalProperty, changeType: 'unchanged', id: "00" + String(maxId + 1) };
      }
      return el;
    });

    const finalList: MappingChange[] = [
      ...updatedList,
      {
        id: "add-row",
        venzaProperty: "",
        venzaPropertyId: "",
        pciPortalProperty: "",
        pciPortalPropertyId: "",
        mid: "",
        changeType: "unchanged",
        action: null,
        isAddRow: true,
      },
    ];
    setIsAddingNew(false);
    setNewRow({});
    setChanges(finalList);
  };

  const handleCancelNewAddRow = () => {
    setChanges(prev =>
      prev.map(item =>
        item?.id?.startsWith("new-")
          ? {
            id: "add-row",
            venzaProperty: "",
            venzaPropertyId: "",
            pciPortalProperty: "",
            pciPortalPropertyId: "",
            mid: "",
            changeType: "unchanged",
            action: null,
            isAddRow: true,
          }
          : item
      )
    );
    setIsAddingNew(false);
    setNewRow({});
  }

  const handleNewMapping = (record: MappingChange) => {
    const selectedRecord = initialData.find(data => data.id === record.id);
    if (selectedRecord) {
      const hasChanged = (selectedRecord.pciPortalProperty !== record.pciPortalProperty) || (selectedRecord.venzaProperty !== record.venzaProperty);
      if (hasChanged) {
        setSavedRowConfirmationModalVisible(true);
      } else {
        handleConfirmMappingChange();
      }
    }

    setEditingId(null);
  }

  const resetRows = () => {
    setChanges(tempChanges)
    setEditingId(null)
    setSavedRowConfirmationModalVisible(false)
  }

  const columns: ColumnsType<MappingChange> = [
    {
      title: "Status",
      dataIndex: "action",
      key: "status",
      render: (_: unknown, record: MappingChange) => {
        if (record.isAddRow) {
          return {
            children: (
              <div className="text-center" onClick={handleAddNewMapping}>
                <span data-slot="badge" className="add-new-mapping-button">
                  <Icon type="addNewMapping" />
                  Add New Mapping
                </span>
              </div>
            ),
            props: { colSpan: 4 },
          };
        }

        if (record.changeType === "new")
          return (
            <span data-slot="badge" className="new-button">
              <Icon type="newMapping" />
              New
            </span>
          );
        return (
          <span data-slot="badge" className="complete-button">
            <Icon type="completed" />Mapped
          </span>
        );
      },
    },
    {
      title: "VENZA Property",
      dataIndex: "venzaProperty",
      key: "venzaProperty",
      render: (_: unknown, record: MappingChange) => {
        if ((record.isAddRow || record.id?.startsWith("new")) && !!isAddingNew) {
          return (
            <Select
              placeholder="Select property"
              className="w-220 custom-select"
              dropdownClassName="custom-select-dropdown"
              suffixIcon={<Icon type="selectSuffix" />}
              value={newRow?.venzaProperty || undefined}
              onChange={(val) => setNewRow({ ...newRow, venzaPropertyId: val, venzaProperty: venzaProperties.find((el) => el.id === String(val))?.name })}
            >
              {venzaProperties
                .filter(
                  (el) =>
                    (!changes.filter((e) => e.action !== "remove").find((e) => e.venzaProperty === el.name))
                ).map((v) => (
                  <Option key={v.id} value={Number(v.id)}>
                    {v.name}
                  </Option>
                ))}
            </Select>
          );
        }
        const isDuplicate =
          record.venzaProperty &&
          changes.filter(ch => ch.action !== "remove").filter(
            (item) =>
              item.venzaProperty === record.venzaProperty &&
              item.id !== record.id
          ).length > 0;
        return editingId === record.id ? (
          <Select
            placeholder="Select property"
            className="w-220 custom-select"
            dropdownClassName="custom-select-dropdown"
            value={record.venzaProperty ?? undefined}
            key={record.id}
            optionLabelProp="label"
            suffixIcon={<Icon type="selectSuffix" />}
            onChange={(val) => handleVenzaChange(record, val)}
          >
            {venzaProperties
              .filter(
                (el) =>
                  (!changes.filter((e) => e.action !== "remove").find((e) => e.venzaProperty === el.name))
              ).map((v) => (
                <Option key={Number(v.id)} value={Number(v.id)}>
                  {v.name}
                </Option>
              ))}
          </Select>
        ) : (
          <>
            {record.venzaProperty}
            {isDuplicate &&
              <p className="duplicate-info">Duplicate mapping</p>}</>
        );
      },
    },
    {
      title: "PCI Portal Property",
      dataIndex: "pciPortalProperty",
      key: "pciPortalProperty",
      render: (_: unknown, record: MappingChange) => {
        if ((record.isAddRow || record.id?.startsWith("new")) && !!isAddingNew) {
          return (
            <Select
              showSearch
              placeholder="Select property"
              className="w-220 custom-select"
              dropdownClassName="custom-select-dropdown"
              suffixIcon={<Icon type="selectSuffix" />}
              value={newRow?.pciPortalProperty || undefined}
              onSearch={(value) => {
                debouncedSearch(value);
              }}
              onChange={(val) => setNewRow({ ...newRow, pciPortalProperty: pciPortalProperties.find(e => e.mid === val)?.name, pciPortalPropertyId: val, mid: val })}
              filterOption={false}
            >
              {pciPortalProperties
                .filter(
                  (el) =>
                    !changes
                      .filter((e) => e.action !== "remove")
                      .find((e) => e.pciPortalProperty === el.name)
                )
                .map((p) => (
                  <Option key={p.mid} value={p.mid}>
                    {p.name}
                  </Option>
                ))}
            </Select>
          );
        }

        const isDuplicate =
          record.pciPortalProperty &&
          changes.filter(ch => ch.action !== "remove").filter(
            (item) =>
              item.pciPortalProperty === record.pciPortalProperty &&
              item.id !== record.id
          ).length > 0;

        return editingId === record.id ? (
          <Select
            showSearch
            placeholder="Select property"
            className="w-220 custom-select"
            dropdownClassName="custom-select-dropdown"
            suffixIcon={<Icon type="selectSuffix" />}
            value={record?.pciPortalProperty || undefined}
            onSearch={(value) => {
              debouncedSearch(value);
            }}
            onChange={(val) => handlePciChange(record, val)}
            filterOption={false}
          >
            {pciPortalProperties
              .filter(
                (el) =>
                  !changes
                    .filter((e) => e.action !== "remove")
                    .find((e) => e.pciPortalProperty === el.name)
              )
              .map((p) => (
                <Option key={p.mid} value={p.mid}>
                  {p.name}
                </Option>
              ))}
          </Select>
        ) : (
          <>
            {record.pciPortalProperty}
            {isDuplicate &&
              <p className="duplicate-info">Duplicate mapping</p>}</>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: MappingChange) => {
        if (record.isAddRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          }
        }
        if (record.id?.startsWith("new")) {
          const disabledButton = !!newRow?.pciPortalProperty && !!newRow?.venzaProperty;
          return (
            <Space>
              <Button
                type="default"
                className="create-mapping-button mr-0"
                disabled={!disabledButton}
                onClick={() => handleNewAddRow({ ...record, pciPortalProperty: newRow?.pciPortalProperty, mid: newRow?.mid, venzaProperty: newRow?.venzaProperty })}
                style={{ opacity: !disabledButton ? 0.5 : 1, backgroundColor: !disabledButton ? "#030213ff" : "white", color: !disabledButton ? "white" : "rgb(3, 2, 19)" }}
              >
                Add
              </Button>
              <Button
                type="default"
                className="create-mapping-button mr-0"
                onClick={() => handleCancelNewAddRow()}
              >
                Cancel
              </Button>
            </Space>);
        }

        return (
          <Space>
            {record.id === editingId && <><Button
              type="default"
              className="create-mapping-button bg-primary color-white"
              onClick={() => handleNewMapping(record)}
            >
              Save
            </Button>
              <Button
                type="default"
                className="create-mapping-button mr-0"
                onClick={() => {
                  setEditingId(null)
                  setChanges([...tempChanges])
                }}
              >
                Cancel
              </Button></>}

            {editingId !== record.id && (record.changeType !== "new") && (
              <Button
                type="default"
                className="create-mapping-button"
                onClick={() => {
                  handlePciPropertySearch("");
                  setTempChanges(changes)
                  setEditingId(record.id ?? "");
                }}
                icon={
                  <Icon type="mappingEdit" />
                }
              >
                Edit
              </Button>
            )}

            {
              (!initialData.find(ch => ch.id === record.id) && record.changeType !== "new") ? <Button
                type="default"
                className="create-mapping-button mr-0"
                onClick={() => {
                  setChanges(changes.map(c => c.id === record.id ? { ...c, changeType: "new" } : { ...c }));
                }}
              >
                Undo
              </Button> :
                (
                  record.action !== "remove" && editingId !== record.id) && (
                  <Button
                    type="default"
                    className="create-mapping-button mr-0"
                    onClick={() => {
                      setChanges(changes.map(c => c.id === record.id ? { ...c, action: "remove" } : { ...c }));
                    }}
                    icon={<MinusCircleOutlined className="w-1 h-1" />}
                  >
                    Remove
                  </Button>


                )

            }

          </Space>
        );
      }
    },
  ];

  const removedColumns = [
    {
      title: "Status",
      key: "status",
      render: () => (
        <Tag
          icon={<MinusCircleOutlined />}
          className="ignored-button"
        >
          Removed
        </Tag>
      ),
    },
    {
      title: "VENZA Property",
      dataIndex: "venzaProperty",
      key: "venzaProperty",
      render: (text: string) =>
        text ? (
          text
        ) : (
          <span className="italic-font">Not mapped</span>
        ),
    },
    {
      title: "PCI Portal Property",
      dataIndex: "pciPortalProperty",
      key: "pciPortalProperty",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: MappingChange) => (
        <Space>
          <Button
            type="default"
            className="create-mapping-button"
            onClick={() => {
              setChanges(
                changes.map(el =>
                  el.id === record.id
                    ? { ...el, action: "confirm" as MappingChange["action"] }
                    : el
                )
              );
            }}
          >
            Restore
          </Button>
        </Space>
      ),
    },
  ];

  const hasDuplicateVenza = !!changes.filter(el => el.action === "remove").find((item) =>
    changes.filter(el => el.action === "remove").find(
      (other) =>
        other.id !== item.id &&
        other.venzaProperty &&
        other.venzaProperty === item.venzaProperty
    )
  );

  const hasDuplicatePci = !!changes.filter(el => el.action === "remove").find((item) =>
    changes.filter(el => el.action === "remove").find(
      (other) =>
        other.id !== item.id &&
        other.pciPortalProperty &&
        other.pciPortalProperty === item.pciPortalProperty
    )
  );

  const disabledPropertyButton = hasDuplicateVenza || hasDuplicatePci;
  useEffect(() => {
    if (!initialData) return;

    const hasAddRow = initialData.some(row => row.id === "add-row");

    const addRow: MappingChange = {
      id: "add-row",
      venzaProperty: "",
      venzaPropertyId: "",
      pciPortalProperty: "",
      pciPortalPropertyId: "",
      mid: "",
      changeType: "unchanged",
      action: null,
      isAddRow: true,
    };

    const finalData = hasAddRow
      ? [...initialData]
      : [...initialData, addRow];

    setChanges(finalData);
    setTempChanges(finalData);
    setNewRow({});
    setEditingId(null);
    setIsAddingNew(false);
    if (initialData.length > 0) {
      setLoading(false);
    }
  }, [initialData]);

  const handlePciPropertySearch = async (search: string) => {
    try {
      const properties = await getExternalMerchants(
        Number(tenantInfo.id),
        Number(tenantInfo.providerId),
        search ?? ""
      );
      setPciPortalProperties(properties);
    } catch (error) {
      message.error("Failed to load properties for mapping.");
    }
  };

  useEffect(() => {
    handlePciPropertySearch("");
  }, []);

  return (
    <Card className="reviewMappingCard">
      <div className="mbx-16">
        <Title level={3} className="headerTitle">Review Property Mapping Changes</Title>
        <p className="headerSubtitle">See current mappings and resolve any changes</p>
      </div>

      <div className="tenantInfo">
        <strong>Client:</strong>
        <span className="text-muted-foreground">{tenantInfo.name}</span>
      </div>

      <div className="grid-button mbx-20">
        {/* New */}

        <Card className="statusCard newCard" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader">
            <PlusOutlined className="new-card-icon" />
            <p>New Properties</p>
          </div>
          <p className="cardValue">{changes.filter(nc => !unChangedData.find(ic => ic.id === nc.id)).filter(e => !!e.pciPortalProperty && !!e.venzaProperty).length}</p>
        </Card>

        {/* Pending */}
        <Card className="statusCard pendingCard" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader">
            <ExclamationCircleOutlined className="cardIcon" />
            <p>Needs Review</p>
          </div>
          <p className="cardValue">{pendingCount}</p>
        </Card>


        <Card className="statusCard ignoredCard" hoverable onClick={() => setIsRemovedModalVisible(true)} bodyStyle={{ padding: 16 }}>
          <div className="cardHeader">
            <MinusCircleOutlined className="cardIcon" />
            <p>Removed</p>
          </div>
          <p className="cardValue">{removedCount}</p>
        </Card>
      </div>

      <Tabs defaultActiveKey="all" className="custom-pill-tabs">
        <TabPane tab={`All (${visibleChanges.length})`} key="all">
          <Spin spinning={loading}> <Table rowKey="id" dataSource={changes.filter(el => el.action !== 'remove')} columns={columns} pagination={false} /></Spin>
        </TabPane>

        <TabPane tab={`New (${newCount})`} key="new">
          <Spin spinning={loading}> <Table rowKey="id" dataSource={changes.filter(nc => !initialData.find(ic => ic.id === nc.id)).filter(e => !!e.pciPortalProperty && !!e.venzaProperty)} columns={columns} pagination={false} /></Spin>
        </TabPane>

        <TabPane tab={`Mapped (${unchangedMappings.length})`} key="mapped">
          <Spin spinning={loading}> <Table rowKey="id" dataSource={changes.filter(nc => !!initialData.find(ic => ic.id === nc.id))} columns={columns} pagination={false} /></Spin>
        </TabPane>
      </Tabs>

      <div className="footerButtons">
        <Button className="backButton" onClick={onBack}>Back</Button>
        <Button className={`continueButton ${disabledPropertyButton ? "opacity-50" : ""}`} type="primary" onClick={handleContinue} disabled={!!disabledPropertyButton}>
          {!!disabledPropertyButton ? "Resolve duplicate mappings to continue" : "Continue"}
        </Button>
      </div>

      {/* Ignored Modal */}
      <Modal visible={isRemovedModalVisible} className="ignoredMappings" onCancel={() => setIsRemovedModalVisible(false)} footer={null} closeIcon={<span className="closeIcon">×</span>}>
        <div className="modalHeader">
          <Title level={3} className="modalTitle fsx-18 card-foreground">Removed Changes</Title>
          <p className="modalSubtitle text-muted-foreground mbx-32">These mappings have been removed. You can restore them to review if needed.</p>
        </div>
        {changes.filter(dt => dt.action === "remove").length > 0 ?
          <Table rowKey="id" dataSource={changes.filter(dt => dt.action === "remove")} columns={removedColumns} pagination={false} />
          : <div className="mtx-16 pb-2 pt-2"><div className="text-center text-gray-400 py-8 text-muted-foreground ignore-modal">
            <MinusCircleOutlined />
            <p className="fs-1 text-muted-foreground">No removed changes</p></div></div>}
      </Modal>

      {/* Confirm Mapping Modal */}
      <Modal className="confirmMapping" title="Confirm Mapping Change" visible={savedRowConfirmationModalVisible} onCancel={resetRows} closable={false} footer={null} centered>
        <form className="confirmMappingForm" onSubmit={(e) => { e.preventDefault(); handleConfirmMappingChange(); }}>
          <p className="confirmMappingDescription">Changing an existing mapping will affect historical results. Are you sure?</p>

          <div className="confirmButtonsWrapper d-flex justify-end mtx-16 gapx-8">
            <Button className="backButton" onClick={resetRows}>Go Back</Button>
            <Button htmlType="submit" className="confirmButton" type="primary">Save Change</Button>
          </div>
        </form>
      </Modal>
    </Card>

  );
};
